import { Box, Tab, Tabs, ThemeProvider } from '@mui/material';
import { OAuthPage } from 'admin/components/OAuthPage';
import { MemreFlexBox, MemrePageContainer, MemreText } from 'components/core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { DepartmentsConfig } from 'pages/admin-dashboard/departments/DepartmentsConfig';
import { ApiKeysManagement } from 'pages/admin-dashboard/partner-config/ApiKeysManagement';
import { AppBrandingConfig } from 'pages/admin-dashboard/partner-config/AppBrandingConfig';
import { CourseEnrollmentLinks } from 'pages/admin-dashboard/partner-config/CourseEnrollmentLinks';
import { CourseLibrary } from 'pages/admin-dashboard/partner-config/CourseLibrary';
import { CoursePaymentSettings } from 'pages/admin-dashboard/partner-config/CoursePaymentSettings';
import { CustomUserFields } from 'pages/admin-dashboard/partner-config/CustomUserFields';
import { DefaultGoalLevel } from 'pages/admin-dashboard/partner-config/DefaultGoalLevel';
import { LearnerExperience } from 'pages/admin-dashboard/partner-config/LearnerExperience';
import { MemberIdSlug } from 'pages/admin-dashboard/partner-config/MemberIdSlug';
import { OrganizationDetails } from 'pages/admin-dashboard/partner-config/OrganizationDetails';
import { ProductNotifications } from 'pages/admin-dashboard/partner-config/ProductNotifications';
import theme from 'pages/learner-dashboard/visulisations/theme';
import React, { useState } from 'react';

const MainConfig: React.FC = () => (
  <Box sx={{ padding: '20px' }}>
    <OrganizationDetails />
    <MemberIdSlug />
    <CourseLibrary />
    <ProductNotifications />
    <CourseEnrollmentLinks />
    <CurrentPartnerPaymentSettings />
    <DefaultGoalLevel />
    <LearnerExperience />
    <CustomUserFields />
  </Box>
);

const OAuthConfigContainer: React.FC = () => {
  const user = useCurrentUser();
  const partnerSlug = user?.primaryPartner.slug;
  return <OAuthPage match={{ params: { partnerSlug } }} isNewAdminPages={true} />;
};
const DepartmentsConfigContainer: React.FC = () => {
  const user = useCurrentUser();

  if (!user?.primaryPartner.partnerSettings.departmentsEnabled) {
    return <></>;
  }

  return (
    <Box sx={{ padding: '20px' }}>
      <DepartmentsConfig />
    </Box>
  );
};

const AppBrandingConfigContainer: React.FC = () => (
  <Box sx={{ padding: '20px' }}>
    <AppBrandingConfig />
  </Box>
);

const ApiKeysConfigContainer: React.FC = () => {
  const user = useCurrentUser();

  if (!user?.primaryPartner.partnerSettings.allowApiAccess) {
    return <></>;
  }

  return <ApiKeysManagement />;
};

// Helper component to conditionally render CoursePaymentSettings
const CurrentPartnerPaymentSettings: React.FC = () => {
  const user = useCurrentUser();
  return user?.primaryPartner.partnerSettings.paymentsEnabled ? <CoursePaymentSettings /> : null;
};

// PartnerConfig Component with Tabs
export const PartnerConfig: React.FC = () => {
  const user = useCurrentUser();
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') ?? 'main';
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  // Define available tabs based on enabled features
  const availableTabs = [
    { key: 'main', label: 'Main' },
    { key: 'oauth', label: 'OAuth' },
    ...(user?.primaryPartner.partnerSettings.departmentsEnabled ? [{ key: 'departments', label: 'Departments' }] : []),
    ...(user?.primaryPartner.partnerSettings.allowApiAccess ? [{ key: 'apikeys', label: 'API Keys' }] : []),
    { key: 'appbranding', label: 'App Branding' },
  ];

  return (
    <ThemeProvider theme={theme}>
      <MemrePageContainer>
        <MemreFlexBox gap={2} direction="column" sx={{ padding: '1rem' }}>
          <MemreText variant="h3">Account Configuration</MemreText>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Partner Configuration Tabs"
              textColor="secondary"
              indicatorColor="secondary"
            >
              {availableTabs.map((tab) => (
                <Tab key={tab.key} label={tab.label} value={tab.key} />
              ))}
            </Tabs>
          </Box>

          {activeTab === 'main' && <MainConfig />}
          {activeTab === 'oauth' && <OAuthConfigContainer />}
          {activeTab === 'departments' && <DepartmentsConfigContainer />}
          {activeTab === 'apikeys' && <ApiKeysConfigContainer />}
          {activeTab === 'appbranding' && <AppBrandingConfigContainer />}
        </MemreFlexBox>
      </MemrePageContainer>
    </ThemeProvider>
  );
};
